import { useEffect,useState } from "react";
import DeleteIcon from "src/assets/all-icons/delete-icon.jsx";

import useTranslations from '@/i18n/useTranslations';
import XIcon from "@/shared-components/icons/components/public/XIcon";
import TextInput from '@/shared-components/inputs/components/new/TextInput.jsx';
import ResizableTextarea from '@/shared-components/inputs/components/ResizableTextarea';
import Text from '@/shared-components/text/components/Text';

import "../styles/PollQuizCreationPopup.css";
import "./styles/base.css";
import "./styles/creation.css";

const PollCreation = ({ onClose, onDelete, onSubmit, initialPollData }) => {
  const t = useTranslations('common.forms.poll_quiz_creation');
  // Initialize with initialPollData if available (for editing), otherwise defaults for a new poll
  const [pollQuestion, setPollQuestion] = useState(initialPollData ? initialPollData.question : "");
  const [pollOptions, setPollOptions] = useState(
    initialPollData ? initialPollData.options : ["", ""] // Start with two empty options if no data provided
  );
  const [isValid, setIsValid] = useState(
    initialPollData
      ? initialPollData.options.filter(o => o.trim() !== "").length >= 2
        && initialPollData.question.trim() !== ""
      : false
  );

  // NEW: Update local state whenever initialPollData changes.
  useEffect(() => {
    if (initialPollData) {
      setPollQuestion(initialPollData.question);
      setPollOptions(initialPollData.options);
      setIsValid(initialPollData.options.filter(option => option.trim() !== "").length >= 2);
    } else {
      setPollQuestion("");
      setPollOptions(["", ""]);
      setIsValid(false);
    }
  }, [initialPollData]);

  // Handle question input
  const handleQuestionChange = (e) => {
    const value = e.target.value.slice(0, 140);
    setPollQuestion(value);
    validatePoll(pollOptions, value);
  };

  // Handle option input
  const handleOptionChange = (index, value) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value.slice(0, 200);
    setPollOptions(newOptions);
    validatePoll(newOptions);
  };

  // now requires ≥2 filled options AND a non-empty question
  const validatePoll = (options, questionText = pollQuestion) => {
    const filledOptions = options.filter(o => o.trim() !== "");
    setIsValid(
      filledOptions.length >= 2
      && questionText.trim() !== ""
    );
  };

  // Add a new option (if less than 4 options exist)
  const handleAddOption = () => {
    if (pollOptions.length < 4) {
      setPollOptions([...pollOptions, ""]); // Add a new empty option
    }
  };

  // Remove an option at a specific index
  const handleRemoveOption = (index) => {
    const newOptions = pollOptions.filter((_, i) => i !== index);
    setPollOptions(newOptions);
    validatePoll(newOptions);
  };

  // Handle poll submission (for both new polls and updates)
  const handleSubmit = () => {
    if (isValid) {
      const filledOptions = pollOptions.filter((option) => option.trim() !== "");
      // Include the poll id if editing an existing poll
      onSubmit({ pollId: initialPollData?.pollId, question: pollQuestion, options: filledOptions });
      onClose();
    }
  };
  
  return (
    <div className="poll-quiz-creation-popup__body">
      <div className="generic-popup__header poll-quiz-creation-popup__header">
        <Text className="generic-popup__header-text-theme poll-quiz-creation-popup__title">
          {initialPollData ? t('edit_poll') : t('create_poll')}
        </Text>
        {onDelete && (
          <DeleteIcon className="poll-quiz-creation-popup__delete" onClick={onDelete} />
        )}
        <XIcon className="generic-popup__close" onClick={onClose} />
      </div>

      <div className="poll-quiz-creation-popup__form">
        {/* Poll Question Input */}
        <ResizableTextarea
          className="poll-question-input"
          placeholder={t('poll_question_placeholder')}
          element="body-text_article"
          maxChar={140}
          ignoreHeader={true}
          value={pollQuestion}
          onChange={handleQuestionChange}
          onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
          onKeyPress={e => { if (e.key === 'Enter') e.preventDefault(); }}
          onBeforeInput={e => {
            // modern browsers fire this for any “insertLineBreak” attempt
            if (e.inputType === 'insertLineBreak') e.preventDefault();
          }}
        />

        <div className="poll-creation__content">
          {/* Poll Options */}
          <div className="poll-options-container">
            {pollOptions.map((option, index) => (
              <div key={index} className="poll-option">
                <div className="poll-option-input-wrapper">
                  {/* + prepend letter */}
                  <span className="poll-option-circle">
                    {String.fromCharCode(65 + index)}
                  </span>
                  <TextInput
                    type="textarea"
                    name={`option-${index}`}
                    label={t('option_label', { number: index + 1 })}
                    placeholder=""
                    value={option}
                    onChange={e => handleOptionChange(index, e.target.value)}
                    maxLength={150}
                    rows={1}
                    maxRows={3}        // limit to 3 rows maximum
                  />
                </div>
                {/* Show delete button for options 3 and 4 */}
                {pollOptions.length > 2 && index >= 2 && (
                  <DeleteIcon
                    className="poll-option-delete"
                    onClick={() => handleRemoveOption(index)}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Add New Option Button */}
          {pollOptions.length < 4 && (
            <div className="poll-add-option-container">
              <button className="poll-add-option" onClick={handleAddOption}>
                {t('add_option')}
              </button>
            </div>
          )}

          {/* Submit Poll Button */}
          <button className="poll-submit-button" onClick={handleSubmit} disabled={!isValid}>
            {t('submit_poll')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PollCreation;
